import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiDel, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import {
  FileText, ShieldCheck, CheckCircle2, Clock, Eye, Download, Upload, X, AlertCircle, Trash2, Edit2
} from 'lucide-react';
import { formatDate } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORY_COLORS = {
  Leave: 'bg-blue-50 text-blue-700 border-blue-200',
  'Payroll & Compliance': 'bg-amber-50 text-amber-700 border-amber-200',
  Benefits: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  General: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function Policies() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // policy being viewed in drawer
  const [pdfUrl, setPdfUrl] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [auditPolicy, setAuditPolicy] = useState(null);
  const [auditData, setAuditData] = useState(null);

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isHRAdmin = role === 'super_admin' || role === 'admin' || dept === 'HR' || dept === 'MD' || role === 'hr' || role === 'md';

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/hr/policies');
      setPolicies(data.policies || []);
    } catch (err) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openPolicy = async (p) => {
    setSelected(p);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token') || '';
      const url = `${apiBase}/api/hr/policies/${p.id}/download`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      toast.error('Could not load policy PDF');
    }
  };

  const closePolicy = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl('');
    setSelected(null);
  };

  const acknowledge = async (p) => {
    try {
      await apiPost(`/hr/policies/${p.id}/acknowledge`, {});
      toast.success(`Acknowledged: ${p.title}`);
      load();
      if (selected?.id === p.id) closePolicy();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Acknowledge failed');
    }
  };

  const deactivate = async (p) => {
    if (!window.confirm(`Deactivate "${p.title}"? It will be hidden from employees but the audit trail is preserved.`)) return;
    try {
      await apiDel(`/hr/policies/${p.id}`);
      toast.success('Policy deactivated');
      load();
    } catch (err) {
      toast.error('Failed to deactivate');
    }
  };

  const openAudit = async (p) => {
    setAuditPolicy(p);
    setAuditData(null);
    try {
      const data = await apiGet(`/hr/policies/${p.id}/acknowledgements`);
      setAuditData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit');
    }
  };

  const pendingCount = policies.filter((p) => !p.my_ack_current).length;

  return (
    <div className="p-6 md:p-8" data-testid="hr-policies-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People &amp; HR</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Company Policies</h1>
          <p className="text-sm text-slate-600 mt-1">
            Read, understand and acknowledge every policy.
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm font-display font-bold text-[10px] uppercase tracking-wider">
                <Clock size={11} /> {pendingCount} pending
              </span>
            )}
          </p>
        </div>
        {isHRAdmin && (
          <button
            onClick={() => setShowUpload(true)}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="upload-policy-btn"
          >
            <Upload size={15} /> Upload Policy
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-sm text-slate-500 font-mono">Loading policies…</div>
      ) : policies.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500 border border-dashed border-slate-200 rounded-sm">
          No policies published yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {policies.map((p) => (
            <div
              key={p.id}
              className={`bg-white border rounded-sm p-5 hover:border-[#3b4872]/50 transition-colors flex flex-col ${p.my_ack_current ? 'border-slate-200' : 'border-amber-300 ring-1 ring-amber-100'}`}
              data-testid={`policy-card-${p.slug || p.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className={`text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${CATEGORY_COLORS[p.category] || CATEGORY_COLORS.General}`}>
                  {p.category}
                </span>
                {p.my_ack_current ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700">
                    <CheckCircle2 size={12} /> Acknowledged
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider text-amber-700">
                    <Clock size={12} /> Pending
                  </span>
                )}
              </div>

              <div className="flex items-start gap-3 mb-2">
                <FileText size={20} className="text-[#3b4872] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-base text-[#0F172A] leading-tight">{p.title}</h3>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Version {p.version}
                    {p.effective_from && ` · Effective ${p.effective_from}`}
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-600 leading-relaxed mb-3 line-clamp-4">{p.description}</p>

              {p.my_ack_at && (
                <div className="text-[10px] text-slate-400 font-mono mb-3">
                  {p.my_ack_current ? `You acknowledged on ${formatDate(p.my_ack_at)}` : `Last ack: ${formatDate(p.my_ack_at)} · re-acknowledge new version`}
                </div>
              )}

              <div className="mt-auto pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => openPolicy(p)}
                  className="text-xs font-display font-bold uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm"
                  data-testid={`view-policy-${p.slug || p.id}`}
                >
                  <Eye size={12} /> Read
                </button>
                {!p.my_ack_current && (
                  <button
                    onClick={() => acknowledge(p)}
                    className="text-xs font-display font-bold uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                    data-testid={`ack-policy-${p.slug || p.id}`}
                  >
                    <CheckCircle2 size={12} /> Acknowledge
                  </button>
                )}
                {isHRAdmin && (
                  <>
                    <button
                      onClick={() => openAudit(p)}
                      className="ml-auto text-xs font-display font-bold uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm"
                      title="View acknowledgement audit"
                      data-testid={`audit-policy-${p.slug || p.id}`}
                    >
                      <ShieldCheck size={12} /> Audit
                    </button>
                    <button
                      onClick={() => deactivate(p)}
                      className="text-slate-400 hover:text-red-600 p-1.5"
                      title="Deactivate policy"
                      data-testid={`delete-policy-${p.slug || p.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reader drawer */}
      {selected && (
        <PolicyReader
          policy={selected}
          pdfUrl={pdfUrl}
          onClose={closePolicy}
          onAcknowledge={() => acknowledge(selected)}
        />
      )}

      {/* HR audit drawer */}
      {auditPolicy && (
        <PolicyAudit
          policy={auditPolicy}
          data={auditData}
          onClose={() => { setAuditPolicy(null); setAuditData(null); }}
        />
      )}

      {/* Upload modal */}
      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)} onUploaded={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}


function PolicyReader({ policy, pdfUrl, onClose, onAcknowledge }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex z-50" data-testid="policy-reader-drawer">
      <div className="ml-auto bg-white w-full md:w-3/4 lg:w-2/3 h-full flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872] mb-1">{policy.category} · {policy.version}</div>
            <h2 className="font-display text-xl font-bold truncate">{policy.title}</h2>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{policy.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!policy.my_ack_current && (
              <button
                onClick={onAcknowledge}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm"
                data-testid="reader-acknowledge-btn"
              >
                <CheckCircle2 size={13} /> Acknowledge
              </button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-slate-700" data-testid="reader-close-btn">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden bg-slate-100">
          {pdfUrl ? (
            <iframe
              title={policy.title}
              src={pdfUrl}
              className="w-full h-full border-0"
              data-testid="policy-pdf-frame"
            />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">Loading PDF…</div>
          )}
        </div>
      </div>
    </div>
  );
}


function PolicyAudit({ policy, data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex z-50" data-testid="policy-audit-drawer">
      <div className="ml-auto bg-white w-full md:w-1/2 h-full flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872] mb-1">Acknowledgement Audit</div>
            <h2 className="font-display text-xl font-bold truncate">{policy.title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          {!data ? (
            <div className="text-sm text-slate-500 font-mono">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat label="Acknowledged" value={data.ack_count} accent="text-emerald-700" />
                <Stat label="Total Users" value={data.total_users} accent="text-slate-700" />
                <Stat label="Coverage" value={`${data.coverage_pct}%`} accent="text-[#3b4872]" />
              </div>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full dense-table">
                  <thead><tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Ack At</th>
                    <th>IP</th>
                  </tr></thead>
                  <tbody>
                    {data.acknowledgements.map((a) => (
                      <tr key={`${a.policy_id}-${a.user_email}`}>
                        <td className="font-display font-semibold">{a.user_name || '—'}</td>
                        <td className="text-xs text-slate-600">{a.user_email}</td>
                        <td className="text-xs">{formatDate(a.acknowledged_at)}</td>
                        <td className="text-[10px] font-mono text-slate-400">{a.ip_address || '—'}</td>
                      </tr>
                    ))}
                    {data.acknowledgements.length === 0 && (
                      <tr><td colSpan="4" className="p-6 text-center text-sm text-slate-500">No acknowledgements yet for this version.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-1">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${accent || ''}`}>{value}</div>
    </div>
  );
}


function UploadModal({ onClose, onUploaded }) {
  const [form, setForm] = useState({ title: '', category: 'General', description: '', version: 'v1', effective_from: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Pick a file to upload'); return; }
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/policies/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Upload failed');
      toast.success(`Policy "${data.title}" uploaded`);
      onUploaded();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upload-policy-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-xl w-full">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Upload Policy</h2>
          <button onClick={onClose} className="text-slate-500"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="label">Title *</label>
            <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="policy-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Category *</label>
              <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="policy-category-select">
                {['Leave', 'Payroll & Compliance', 'Benefits', 'Code of Conduct', 'IT Security', 'General'].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Version</label>
              <input className="input" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="v1, v2.1, …" />
            </div>
          </div>
          <div>
            <label className="label">Effective From</label>
            <input type="date" className="input" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief summary shown on the policy card" />
          </div>
          <div>
            <label className="label">File (PDF / DOC / DOCX, max 25MB) *</label>
            <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={(e) => setFile(e.target.files?.[0])} required data-testid="policy-file-input" />
            {file && <div className="text-[11px] text-slate-500 mt-1 font-mono">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800 flex gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>Uploading a new version will require all employees to re-acknowledge.</span>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="policy-upload-submit">
              <Upload size={14} /> {busy ? 'Uploading…' : 'Upload Policy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
